import { verifyOwner } from '../verifyOwner'
export { verifyOwner } from '../verifyOwner'
import {canonicalContextPacket} from './identity'
import type { CanvasDoc } from '../contextSync/legacyCanvas'
import { initialBuild, buildKey, buildPrefix, type ContextBuildState } from '../contextBuild/state'
import { getDatabase, NoteRepository } from '../../data/db'
import { APP_SETTINGS } from '../../config/appSettings'
import type { Note } from '../../domain/note'
import { transitionNoteStatus } from '../../domain/note'
import { GUEST_SCOPE, knownAccountLimits, PlanLimitError } from '../../auth/planLimits'
import type { Context, ReviewPacket } from './model'
import { LAB, MANUAL_SECTION } from './model'
export { MANUAL_SECTION } from './model'

export const MANUAL_CONTEXT_LIMITS = APP_SETTINGS.intelligence.manualContexts
const PREFIX = 'manual-context:v1:'
export interface ManualContext {
  version: 1
  ownerId: string
  id: string
  aliases?: string[]
  title: string
  purpose?: string
  titleOrigin: 'manual' | 'automatic' | 'existing'
  created: boolean
  revision: number
  createdAt: number
  links: { id?: string; noteId: string; lensId: string | null; addedAt: number }[]
  /** Legacy authored sources/purpose and opaque history retained for v2 and sync. */
  canvas?: CanvasDoc
  archivedAt?: number
  demo?: { version: 1; noteIds: string[]; hintsDismissed?: boolean; archivedNotes?: { id: string; revision: string; priorStatus: Note['status'] }[] }
}
const prefix = (owner: string) => `${PREFIX}${encodeURIComponent(owner)}:`
export const manualContextKey = (owner: string, id: string) => prefix(owner) + encodeURIComponent(id)
const key = manualContextKey
const range = (owner: string) => IDBKeyRange.bound(prefix(owner), prefix(owner) + '\uffff')

export function automaticContextName(notes: Note[]): string {
  const first = notes[0]
  if (!first) return 'New context'
  const tags = first.userTags.filter(tag => tag.replace(/^#/, '').toLowerCase() !== LAB.cohortTag
    && notes.every(note => note.userTags.includes(tag)))
  const title = tags.length ? tags[0]!.replace(/^#/, '')
    : notes.length === 1 ? first.manualTitle || first.derivedTitle || 'Saved note'
      : `${first.manualTitle || first.derivedTitle || 'Saved notes'} + ${notes.length - 1} more`
  return title.replace(/\s+/g, ' ').trim().slice(0, MANUAL_CONTEXT_LIMITS.maxNameChars)
}

export async function readManualContexts(owner: string): Promise<ManualContext[]> {
  const db = await getDatabase()
  const tx = db.transaction('meta', 'readonly')
  await verifyOwner(tx.store, owner)
  const rows = await tx.store.getAll(range(owner))
  await tx.done
  return rows.map(row => {
    const value = JSON.parse(String(row.value)) as ManualContext
    if (value.version !== 1 || value.ownerId !== owner || !Array.isArray(value.links)) throw new Error('Saved context data could not be read.')
    return value
  })
}

/** Existing owner-scoped Context records are migration evidence that this device
 * previously used the gated feature. They can restore offline access while the
 * next online sync obtains a current server verdict. */
export async function hasSavedContextData(owner: string): Promise<boolean> {
  const db = await getDatabase()
  const tx = db.transaction('meta', 'readonly')
  await verifyOwner(tx.store, owner)
  const [records, builds] = await Promise.all([
    tx.store.count(range(owner)),
    tx.store.count(IDBKeyRange.bound(buildPrefix(owner), buildPrefix(owner) + '\uffff')),
  ])
  await tx.done
  return records > 0 || builds > 0
}

export interface ContextAdditionReceipt { ownerId: string; contextId: string; linkIds: string[]; createdRevision: number | null; buildRequestId?: string }

export interface AddContextInput {
  ownerId: string
  contextId: string
  noteIds: string[]
  newContext?: { name: string; purpose?: string }
  existingTitle?: string
  existingSourceIds?: string[]
  lensId?: string
}

// One serialized IndexedDB transaction owns header + explicit membership. No source
// text or generated cards are saved here; no review-packet replacement can erase it.
export async function addNotesToContext(input: AddContextInput): Promise<ManualContext & { receipt: ContextAdditionReceipt }> {
  const db = await getDatabase()
  const tx = db.transaction(['meta', 'notes'], 'readwrite')
  const meta = tx.objectStore('meta')
  try {
    await verifyOwner(meta, input.ownerId)
    const ids = [...new Set(input.noteIds)]
    if (!ids.length) throw new Error('Select at least one note.')
    const stored = await meta.get(key(input.ownerId, input.contextId))
    const current = stored ? JSON.parse(String(stored.value)) as ManualContext : undefined
    const existingSourceIds = current || input.newContext ? [] : [...new Set(input.existingSourceIds ?? [])].filter(id => !ids.includes(id))
    const sourceIds = [...existingSourceIds, ...ids]
    const notes = await Promise.all(sourceIds.map(id => tx.objectStore('notes').get(id)))
    if (notes.some(note => !note || note.status === 'trash')) throw new Error('A selected or existing context note is no longer available. Review the sources.')
    if (current && current.ownerId !== input.ownerId) throw new Error('Context account mismatch.')
    if (current?.archivedAt) throw new Error('This Context was deleted. Choose another Context.')
    if (!current && !input.newContext && !input.existingTitle) throw new Error('Choose an available context.')
    if (!current) {
      const activeCount = (await meta.getAll(range(input.ownerId)))
        .filter(row => !(JSON.parse(String(row.value)) as ManualContext).archivedAt).length
      // The plan limit (from the server's catalog, as last told to this device) is checked before the device ceiling.
      const planContexts = knownAccountLimits(input.ownerId)?.contexts
      if (planContexts != null && activeCount >= planContexts) throw new PlanLimitError('contexts', planContexts, activeCount)
      if (activeCount >= MANUAL_CONTEXT_LIMITS.maxContexts) throw new Error('Context limit reached on this device.')
    }
    const name = input.newContext?.name.trim()
    if (name && name.length > MANUAL_CONTEXT_LIMITS.maxNameChars) throw new Error('Context name is too long.')
    const purpose = input.newContext?.purpose?.trim() ?? ''
    if (purpose.length > MANUAL_CONTEXT_LIMITS.maxPurposeChars) throw new Error('Context purpose is too long.')
    const now = Date.now()
    const record: ManualContext = current ?? {
      version: 1, ownerId: input.ownerId, id: input.contextId,
      title: input.newContext ? name || purpose.replace(/\s+/g, ' ').slice(0, MANUAL_CONTEXT_LIMITS.maxNameChars) || automaticContextName(notes as Note[]) : input.existingTitle!,
      ...(input.newContext ? { purpose } : {}),
      titleOrigin: input.newContext ? name || purpose ? 'manual' : 'automatic' : 'existing',
      created: Boolean(input.newContext), revision: 0, createdAt: now,
      links: existingSourceIds.map(noteId => ({ id: crypto.randomUUID(), noteId, lensId: null, addedAt: now })),
    }
    const added = ids.filter(id => !record.links.some(link => link.noteId === id && link.lensId === (input.lensId ?? null)))
    if (record.links.length + added.length > MANUAL_CONTEXT_LIMITS.maxLinkedNotes) throw new Error(`A context can hold up to ${MANUAL_CONTEXT_LIMITS.maxLinkedNotes} explicit note links. Nothing was added.`)
    // Notes per Context: growth past the plan limit is refused; a Context already over it can still be edited.
    const planNotes = knownAccountLimits(input.ownerId)?.notesPerContext
    if (planNotes != null && added.length) {
      const before = new Set(record.links.map(link => link.noteId)).size
      const after = new Set([...record.links.map(link => link.noteId), ...added]).size
      if (after > planNotes && after > before) throw new PlanLimitError('notesPerContext', planNotes, after)
    }
    const newLinks = added.map(noteId => ({ id: crypto.randomUUID(), noteId, lensId: input.lensId ?? null, addedAt: now }))
    let buildRequestId: string | undefined
    if (added.length || !current) {
      record.links.push(...newLinks)
      record.revision++
      await meta.put({ key: key(input.ownerId, input.contextId), value: JSON.stringify(record) })
    }
    if (!current && input.newContext) {
      await meta.put({ key: buildKey(input.ownerId, input.contextId), value: JSON.stringify(initialBuild(input.ownerId, input.contextId, ids)) })
    } else if (added.length) {
      const buildRow = await meta.get(buildKey(input.ownerId, input.contextId))
      const previous = buildRow ? JSON.parse(String(buildRow.value)) as ContextBuildState : undefined
      const base = previous ?? initialBuild(input.ownerId, input.contextId, record.links.map(link => link.noteId))
      buildRequestId = base.status === 'queued' ? base.requestId : crypto.randomUUID()
      const queued: ContextBuildState = {
        ...base,
        requestId: buildRequestId,
        requestOrigin: 'membership',
        refreshMode: 'full',
        status: 'queued',
        requestedAt: now,
        startedAt: undefined,
        sourceIds: [...new Set(record.links.map(link => link.noteId))],
        error: undefined,
        candidate: undefined,
        revision: base.revision + 1,
      }
      await meta.put({ key: buildKey(input.ownerId, input.contextId), value: JSON.stringify(queued) })
    }
    await tx.done
    signalChange(input.ownerId)
    return { ...record, receipt: { ownerId: input.ownerId, contextId: record.id, linkIds: newLinks.map(link => link.id), createdRevision: !current && input.newContext ? record.revision : null, ...(buildRequestId ? { buildRequestId } : {}) } }
  } catch (error) {
    try { tx.abort() } catch { /* It may already have aborted. */ }
    await tx.done.catch(() => undefined)
    throw error
  }
}

export interface CreateEmptyContextInput { ownerId: string; contextId: string; name: string; purpose?: string }

/**
 * A new Context with no notes yet: the person adds the first one from inside it. The same account check, plan limit and size checks
 * as creating one with notes (`addNotesToContext`), but no view is queued, because there is nothing to read. The first note added
 * goes through `addNotesToContext`, which then queues the view.
 */
export async function createEmptyContext(input: CreateEmptyContextInput): Promise<ManualContext> {
  const db = await getDatabase()
  const tx = db.transaction('meta', 'readwrite')
  try {
    await verifyOwner(tx.store, input.ownerId)
    const name = input.name.trim()
    if (!name) throw new Error('Give the Context a name.')
    if (name.length > MANUAL_CONTEXT_LIMITS.maxNameChars) throw new Error('Context name is too long.')
    const purpose = input.purpose?.trim() ?? ''
    if (purpose.length > MANUAL_CONTEXT_LIMITS.maxPurposeChars) throw new Error('Context purpose is too long.')
    if (await tx.store.get(key(input.ownerId, input.contextId))) throw new Error('This Context already exists.')
    const activeCount = (await tx.store.getAll(range(input.ownerId)))
      .filter(row => !(JSON.parse(String(row.value)) as ManualContext).archivedAt).length
    const planContexts = knownAccountLimits(input.ownerId)?.contexts
    if (planContexts != null && activeCount >= planContexts) throw new PlanLimitError('contexts', planContexts, activeCount)
    if (activeCount >= MANUAL_CONTEXT_LIMITS.maxContexts) throw new Error('Context limit reached on this device.')
    const record: ManualContext = {
      version: 1, ownerId: input.ownerId, id: input.contextId, title: name, ...(purpose ? { purpose } : {}),
      titleOrigin: 'manual', created: true, revision: 1, createdAt: Date.now(), links: [],
    }
    await tx.store.put({ key: key(input.ownerId, input.contextId), value: JSON.stringify(record) })
    await tx.done
    signalChange(input.ownerId)
    return record
  } catch (error) {
    try { tx.abort() } catch { /* It may already have aborted. */ }
    await tx.done.catch(() => undefined)
    throw error
  }
}

export async function undoContextAddition(receipt: ContextAdditionReceipt): Promise<void> {
  const db = await getDatabase()
  const tx = db.transaction('meta', 'readwrite')
  try {
    await verifyOwner(tx.store, receipt.ownerId)
    const entry = await tx.store.get(key(receipt.ownerId, receipt.contextId))
    if (entry) {
      const record = JSON.parse(String(entry.value)) as ManualContext
      const links = record.links.filter(link => !link.id || !receipt.linkIds.includes(link.id))
      if (links.length !== record.links.length) {
        if (!links.length && record.revision === receipt.createdRevision) { await tx.store.delete(entry.key); await tx.store.delete(buildKey(receipt.ownerId, receipt.contextId)) }
        else {
          await tx.store.put({ key: entry.key, value: JSON.stringify({ ...record, links, revision: record.revision + 1 }) })
          if (receipt.buildRequestId) {
            const buildEntry = await tx.store.get(buildKey(receipt.ownerId, receipt.contextId))
            if (buildEntry) {
              const state = JSON.parse(String(buildEntry.value)) as ContextBuildState
              if (state.requestId === receipt.buildRequestId) {
                const currentIds = [...new Set(links.map(link => link.noteId))].sort()
                const activeIds = state.active?.input.sources.map(source => source.id).sort()
                const needsUpdate = !activeIds || JSON.stringify(activeIds) !== JSON.stringify(currentIds)
                const wasClaimed = state.status === 'running' || state.status === 'ready' || state.status === 'applied'
                const next: ContextBuildState = needsUpdate ? {
                  ...state,
                  requestId: wasClaimed ? crypto.randomUUID() : state.requestId,
                  requestOrigin: 'membership',
                  refreshMode: 'full',
                  status: 'queued',
                  requestedAt: Date.now(),
                  startedAt: undefined,
                  sourceIds: currentIds,
                  error: undefined,
                  candidate: undefined,
                  revision: state.revision + 1,
                } : {
                  ...state,
                  requestId: wasClaimed ? crypto.randomUUID() : state.requestId,
                  status: 'applied',
                  startedAt: undefined,
                  sourceIds: currentIds,
                  error: undefined,
                  candidate: undefined,
                  revision: state.revision + 1,
                }
                await tx.store.put({ key: buildEntry.key, value: JSON.stringify(next) })
              }
            }
          }
        }
      }
    }
    await tx.done
    signalChange(receipt.ownerId)
  } catch (error) {
    try { tx.abort() } catch { /* Already aborted. */ }
    await tx.done.catch(() => undefined)
    throw error
  }
}

export async function editManualContext(owner: string, id: string, change: { name: string; expectedRevision?: number; context?: Context } | { purpose: string; expectedRevision?: number } | { removeNoteId: string; lensId: string | null }): Promise<void> {
  const db = await getDatabase()
  const tx = db.transaction('meta', 'readwrite')
  try {
    await verifyOwner(tx.store, owner)
    const stored = await tx.store.get(key(owner, id))
    if (!stored && (!('name' in change) || !change.context || change.context.id !== id)) throw new Error('This context is no longer available.')
    const record: ManualContext = stored ? JSON.parse(String(stored.value)) : {
      version: 1, ownerId: owner, id, title: '', titleOrigin: 'existing', created: false, revision: 0, createdAt: Date.now(), links: [],
    }
    if (record.archivedAt) throw new Error('This Context was deleted. Reopen the collection.')
    if ('name' in change) {
      if ((stored ? record.revision : undefined) !== change.expectedRevision) throw new Error('This context changed. Reload it before renaming.')
      const name = change.name.trim()
      if (!name || name.length > MANUAL_CONTEXT_LIMITS.maxNameChars) throw new Error('Enter a context name within the character limit.')
      record.title = name; record.titleOrigin = 'manual'
    } else if ('purpose' in change) {
      if (!stored || record.revision !== change.expectedRevision) throw new Error('This context changed. Reload it before editing the purpose.')
      const purpose = change.purpose.trim()
      if (purpose.length > MANUAL_CONTEXT_LIMITS.maxPurposeChars) throw new Error('Context purpose is too long.')
      // The owner's words are the purpose; an empty one clears it.
      if (purpose) record.purpose = purpose; else delete record.purpose
    } else record.links = record.links.filter(link => link.noteId !== change.removeNoteId || link.lensId !== change.lensId)
    record.revision++
    await tx.store.put({ key: key(owner, id), value: JSON.stringify(record) })
    await tx.done
    signalChange(owner)
  } catch (error) {
    try { tx.abort() } catch { /* Already aborted. */ }
    await tx.done.catch(() => undefined)
    throw error
  }
}

/** Archive a Context and, only when requested, trash its notes that have no link
 * to another active Context. The Context and note outbox changes commit together. */
export async function deleteManualContext(owner: string, id: string, expectedRevision: number | undefined, trashExclusiveNotes: boolean, deviceId: string, context?: Context, visibleContexts: Context[] = []): Promise<Note[]> {
  const repository = new NoteRepository()
  const contextKey = key(owner, id)
  const raw = await repository.getMeta(contextKey)
  if (typeof raw !== 'string' && (!context || context.id !== id)) throw new Error('This Context is no longer available. Reopen the collection.')
  const now = Date.now()
  const record: ManualContext = typeof raw === 'string' ? JSON.parse(raw) as ManualContext : {
    version: 1, ownerId: owner, id, title: context!.title, titleOrigin: 'existing', created: false,
    revision: 0, createdAt: now,
    links: [...new Set(context!.lenses.flatMap(lens => lens.sections.flatMap(section => section.contributions.flatMap(item => item.sources.map(source => source.id)))))].map(noteId => ({ noteId, lensId: null, addedAt: now })),
  }
  if (record.ownerId !== owner || record.archivedAt) throw new Error('This Context is no longer available. Reopen the collection.')
  if ((typeof raw === 'string' ? record.revision : undefined) !== expectedRevision) throw new Error('This Context changed. Reopen it before deleting.')

  const db = await getDatabase()
  const snapshotRows = await db.getAll('meta', range(owner))
  const snapshot = snapshotRows.map(({ key, value }) => ({ key, value }))
  const records = [...snapshotRows.map(row => JSON.parse(String(row.value)) as ManualContext).filter(item => item.id !== id), record]
  const exclusiveIds = exclusiveContextNoteIds(records, id, context, visibleContexts)
  const sources = trashExclusiveNotes ? await Promise.all(exclusiveIds.map(noteId => db.get('notes', noteId))) : []
  const changes = sources.flatMap(note => note && note.status !== 'trash'
    ? [{ note: transitionNoteStatus(note, 'trash', deviceId), reason: 'delete' as const, expectedRevision: note.revisionId }]
    : [])
  const archived: ManualContext = { ...record, archivedAt: now, demo: undefined, revision: record.revision + 1 }
  await repository.commitLocalNotes(changes, {
    // A guest has no bound account to check; an account's deletion is refused if the account changed.
    key: contextKey, value: JSON.stringify(archived), expected: typeof raw === 'string' ? raw : undefined, ...(owner === GUEST_SCOPE ? {} : { ownerId: owner }),
  }, [], [], { prefix: prefix(owner), entries: snapshot })
  signalChange(owner)
  return changes.map(change => change.note)
}

export function exclusiveContextNoteIds(records: ManualContext[], id: string, fallback?: Context, visibleContexts: Context[] = []): string[] {
  const record = records.find(item => item.id === id && !item.archivedAt)
  if (!record && fallback?.id !== id) return []
  const sharedIds = new Set(records.filter(item => item.id !== id && !item.archivedAt)
    .flatMap(item => item.links.map(link => link.noteId)))
  for (const context of visibleContexts) if (context.id !== id) {
    for (const lens of context.lenses) for (const section of lens.sections) for (const item of section.contributions) for (const source of item.sources) sharedIds.add(source.id)
  }
  const ids = record?.links.map(link => link.noteId) ?? fallback!.lenses.flatMap(lens => lens.sections.flatMap(section => section.contributions.flatMap(item => item.sources.map(source => source.id))))
  return [...new Set(ids)].filter(noteId => !sharedIds.has(noteId))
}

export const MANUAL_CONTEXT_EVENT = 'minimany:manual-context-change'
export function signalChange(owner: string) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(MANUAL_CONTEXT_EVENT, { detail: owner }))
  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel(MANUAL_CONTEXT_EVENT)
    channel.postMessage(owner); channel.close()
  }
}

export function withManualContexts(packet: ReviewPacket, records: ManualContext[], notes: Note[] = []): ReviewPacket {
  const byId = new Map(notes.map(note => [note.id, note]))
  const contexts = [...canonicalContextPacket(packet,records).contexts]
  for (const record of records) {
    const index = contexts.findIndex(context => context.id === record.id)
    const base: Context = contexts[index] ?? {
      id: record.id, title: record.title, purpose: 'Notes you brought together.',
      lenses: [{ id: `${record.id}-notes`, title: 'Notes', purpose: 'Notes you brought together.', sections: [] }],
    }
    const context: Context = { ...base, aliases: [...new Set([...(base.aliases ?? []), ...(record.aliases ?? [])])], ...(record.purpose ? { purpose: record.purpose } : {}), title: record.titleOrigin === 'existing' && index >= 0 ? base.title : record.title,
      lenses: base.lenses.map((lens, i) => {
        const links = [...new Map(record.links.filter(link => link.lensId === lens.id || (i === 0 && (link.lensId === null || !base.lenses.some(view => view.id === link.lensId)))).map(link => [link.noteId, link])).values()]
        const contributions = links.map(link => {
          const note = byId.get(link.noteId)
          return { id: `explicit-${link.noteId}`, title: note?.manualTitle || note?.derivedTitle || 'Unavailable note',
            text: note?.body.slice(0, LAB.sourcePreviewChars) ?? '',
            sources: [{ id: link.noteId, revision: note?.revisionId ?? 'unavailable' }],
            kind: 'fact' as const, basis: 'source' as const, origin: 'manual' as const, fit: 'placed' as const, facts: [],
          }
        })
        return { ...lens, sections: [...lens.sections, ...(links.length || !lens.sections.length ? [{ id: MANUAL_SECTION, title: record.created ? 'Notes' : 'Added notes', layout: 'cards' as const, contributions }] : [])] }
      }),
    }
    if (index >= 0) contexts[index] = context
    else contexts.push(context)
  }
  return { ...packet, contexts: contexts.filter(context => !records.find(record => record.id === context.id)?.archivedAt) }
}
